import {
  AuthProvider,
  ensureAuthenticated,
  FeatureFlagProvider,
  getCurrentUserControllerGetCurrentUserQueryOptions,
} from '@artic/api-client';
import { EnvironmentBanner } from '@artic/ui/environment-banner';
import { useFeatureFlag } from '@artic/ui/feature-flags';
import { FullHeightStack } from '@artic/ui/full-height-layout';
import { clearAuthCookies } from '@artic/ui/lib/auth-cookies';
import { PageHeader } from '@artic/ui/page-header';
import { SidebarInset, SidebarProvider } from '@artic/ui/sidebar';
import { Toaster } from '@artic/ui/toast';
import { TooltipProvider } from '@artic/ui/tooltip';
import type { QueryClient } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { createRootRouteWithContext, Outlet, useRouterState } from '@tanstack/react-router';
import { TanStackRouterDevtools } from '@tanstack/react-router-devtools';
import { NuqsAdapter } from 'nuqs/adapters/tanstack-router';
import { Suspense, type ReactNode } from 'react';

import type { AuthState } from '../auth';
import { getAuthState, redirectToHub } from '../auth';
import { AppLoading } from '../components/app-loading';
import { IntercomIdentity } from '../components/intercom-identity';
import { QualitySidebar } from '../components/quality-sidebar';
import { RouteBreadcrumbs } from '../components/route-breadcrumbs';
import { RouteHeaderActions } from '../components/route-header-actions';
import { SentryIdentity } from '../components/sentry-identity';
import { ThemeProvider } from '../components/theme-provider';
import { usePartnerId } from '../features/inbox/use-partner-name';
import { RealtimeProvider } from '../features/realtime/realtime-provider';
import { useCapacityRealtime } from '../features/realtime/use-capacity-realtime';
import { useChangeControlRealtime } from '../features/realtime/use-change-control-realtime';
import { usePartnerRealtimeSync } from '../features/realtime/use-partner-realtime-sync';
import { I18nPartnerSync, I18nProvider } from '../i18n';

export interface RouterContext {
  queryClient: QueryClient;
  auth: AuthState;
}

const COOKIE_OPTIONS = (() => {
  const domain = import.meta.env['VITE_AUTH_COOKIE_DOMAIN'] as string | undefined;
  return domain ? { domain } : {};
})();

export const Route = createRootRouteWithContext<RouterContext>()({
  beforeLoad: async ({ context: { queryClient } }) => {
    // Read fresh cookie state on every navigation instead of relying on the
    // static snapshot captured at router creation time.
    let auth = getAuthState();

    if (!auth.isAuthenticated) {
      // Neither access token nor refresh token — redirect immediately.
      redirectToHub();
    }

    if (!auth.token) {
      // Access token is missing but refresh token exists — attempt a
      // proactive refresh so the AuthProvider's /current-user call
      // doesn't need the 401 → refresh → retry round-trip.
      const ok = await ensureAuthenticated();
      if (!ok) redirectToHub();

      // Re-read after refresh wrote new cookies.
      auth = getAuthState();
    }

    // Validate the session server-side before rendering. A stored token can
    // outlive its user row (e.g. after `supabase db reset --local`); without
    // this gate the AuthProvider's suspense query throws 404 and the app gets
    // stuck on the root CatchBoundary with no path back to login.
    try {
      await queryClient.ensureQueryData(getCurrentUserControllerGetCurrentUserQueryOptions());
    } catch (error) {
      console.warn('[auth] /current-user rejected, clearing session', error);
      clearAuthCookies(COOKIE_OPTIONS);
      redirectToHub();
    }

    return { auth };
  },
  component: RootLayout,
});

function useLayoutMode() {
  return useRouterState({
    select: (state) => {
      for (let i = state.matches.length - 1; i >= 0; i--) {
        const layout: unknown = state.matches[i]?.staticData.layout;
        if (typeof layout === 'string') return layout;
      }
      return 'default' as const;
    },
  });
}

function FeatureFlagProviderWithPartner({ children }: { children: ReactNode }) {
  const partnerId = usePartnerId();
  return <FeatureFlagProvider partnerId={partnerId}>{children}</FeatureFlagProvider>;
}

/**
 * App-wide bridge: subscribes the current user to their own partner's realtime
 * topic for partner-scoped events such as feature-flag changes and import
 * status updates. Renders nothing. Must sit inside `RealtimeProvider` (for the
 * socket); `AuthProvider` + `FeatureFlagProvider` are ancestors, so
 * `usePartnerId` resolves.
 */
function PartnerRealtimeSync() {
  const partnerId = usePartnerId();
  usePartnerRealtimeSync(partnerId, { enabled: true });
  return null;
}

/**
 * App-wide bridge to the `change_control:<partnerId>` topic: keeps the change
 * control run progress, Bill of Operations matrix staleness and operations
 * staleness caches live for the whole session. Gated on either the
 * change-control or planner feature, so partners without those features open no
 * channel. Renders nothing; must sit inside `RealtimeProvider`.
 */
function ChangeControlRealtimeSync() {
  const partnerId = usePartnerId();
  const changeControlEnabled = useFeatureFlag('change_control');
  const plannerEnabled = useFeatureFlag('planner');
  useChangeControlRealtime(partnerId, {
    enabled: changeControlEnabled || plannerEnabled,
  });
  return null;
}

/**
 * App-wide bridge to the `production_plan:<partnerId>` topic: refetches the
 * ENG-732 capacity matrix (summary cards + expanded view), the planner summary
 * and the planner rows whenever the ENG-721 recompute persists — the
 * split-screen "move a panel, watch the square change" loop. Gated on either
 * the capacity_view or planner feature: ENG-847 made the rows depend on this
 * ping too, and the Production Plan ships to partners without capacity_view.
 * Renders nothing; must sit inside `RealtimeProvider`.
 */
function CapacityRealtimeSync() {
  const partnerId = usePartnerId();
  const capacityViewEnabled = useFeatureFlag('capacity_view');
  const plannerEnabled = useFeatureFlag('planner');
  useCapacityRealtime(partnerId, { enabled: capacityViewEnabled || plannerEnabled });
  return null;
}

function RootLayout() {
  const { auth } = Route.useRouteContext();
  const layoutMode = useLayoutMode();

  return (
    <NuqsAdapter>
      <ThemeProvider>
        <TooltipProvider>
          <I18nProvider>
            <Suspense fallback={<AppLoading />}>
              <AuthProvider activePartner={auth.partner}>
                <I18nPartnerSync />
                <IntercomIdentity />
                <SentryIdentity />
                <FeatureFlagProviderWithPartner>
                  <RealtimeProvider>
                    <PartnerRealtimeSync />
                    <ChangeControlRealtimeSync />
                    <CapacityRealtimeSync />
                    <SidebarProvider>
                      <QualitySidebar />
                      {/*
                       * Full-height routes (Gantt timeline, AG Grid, inbox) scroll
                       * internally, so the inset needs a definite viewport height: the
                       * h-16 PageHeader takes its 64px and FullHeightStack fills the
                       * rest. The sidebar wrapper only supplies `min-h-svh` (a minimum),
                       * so without a fixed height the inset grows with its content and
                       * the page spills past the viewport by the header's height instead
                       * of the inner region scrolling. `overflow-y-auto` (not hidden) so
                       * the odd full-height route that is a tall document (e.g. the
                       * operation editor) still scrolls rather than being clipped; the
                       * internal-scroll routes fit exactly and never show this scrollbar.
                       * Default routes keep ordinary document scroll.
                       */}
                      <SidebarInset
                        className={
                          layoutMode === 'full-height' ? 'h-svh overflow-y-auto' : undefined
                        }
                      >
                        <EnvironmentBanner />
                        <PageHeader actions={<RouteHeaderActions />}>
                          <RouteBreadcrumbs />
                        </PageHeader>
                        {layoutMode === 'full-height' ? (
                          <FullHeightStack>
                            <Outlet />
                          </FullHeightStack>
                        ) : (
                          <div className="flex flex-1 flex-col gap-4 p-4 pt-0">
                            <Outlet />
                          </div>
                        )}
                      </SidebarInset>
                    </SidebarProvider>
                  </RealtimeProvider>
                </FeatureFlagProviderWithPartner>
              </AuthProvider>
            </Suspense>
          </I18nProvider>
          <Toaster position="top-right" richColors closeButton />
          {import.meta.env.DEV && (
            <>
              <ReactQueryDevtools buttonPosition="bottom-left" />
              <TanStackRouterDevtools position="bottom-right" />
            </>
          )}
        </TooltipProvider>
      </ThemeProvider>
    </NuqsAdapter>
  );
}
